import * as React from 'react';

import { CardShellActionButtons, type CardShellAction } from './CardShellActionButtons.js';
import {
  DimeCard,
  DimeCardAction,
  DimeCardContent,
  DimeCardFooter,
  DimeCardHeader,
  DimeCardTitle,
} from './DimeCard.js';
import type { DimeCardShellActivity } from './DimeCardShell.js';
import { DimeDetailRows, type DimeDetailRow, type DimeDetailRowGroup } from './DimeDetailRows.js';
import { buildActivityMetaItems } from './lib/build-activity-meta-items.js';
import { cn } from './lib/utils.js';

export type DimeDetailCardShellAction = CardShellAction;

export type DimeDetailCardShellRow = DimeDetailRow;

export type DimeDetailCardShellSection = Readonly<{
  title: string;
  rows: DimeDetailCardShellRow[];
}>;

/** Per-slot classes, merged after the shell's own so a conflicting class wins, like `className` on the card. */
export type DimeDetailCardShellClassNames = Readonly<
  Partial<Record<'header' | 'title' | 'actions' | 'content' | 'rows' | 'empty' | 'footer', string>>
>;

type DimeDetailCardShellProps = Readonly<
  Omit<React.ComponentProps<typeof DimeCard>, 'children' | 'content' | 'title'> & {
    title: React.ReactNode;
    actions?: DimeDetailCardShellAction[];
    /** Label/value facts -- a DimeCardShell `meta` array feeds it as is; the icon is optional here. */
    rows?: DimeDetailCardShellRow[];
    sections?: DimeDetailCardShellSection[];
    /** Free-form body under the rows (notes, map, contact list). */
    content?: React.ReactNode;
    activity?: DimeCardShellActivity;
    /** Shown in place of the rows when `rows` is empty and no `content` is passed. */
    empty?: React.ReactNode;
    /** `stacked` puts each label above its value, for a narrow rail where inline values wrap mid-word. */
    layout?: 'inline' | 'stacked';
    /** Secondary lines under the body, in the card's footer band. */
    footer?: React.ReactNode;
    classNames?: DimeDetailCardShellClassNames;
  }
>;

export function DimeDetailCardShell({
  title,
  actions = [],
  rows = [],
  sections = [],
  content,
  activity,
  empty,
  layout = 'inline',
  footer,
  className,
  classNames = {},
  ...props
}: DimeDetailCardShellProps) {
  const titleId = React.useId();
  const activityRows = buildActivityMetaItems(activity);
  const groups: DimeDetailRowGroup[] = [
    ...(rows.length > 0 ? [{ rows }] : []),
    ...sections.filter((section) => section.rows.length > 0),
    ...(activityRows.length > 0 ? [{ rows: activityRows }] : []),
  ];

  return (
    <DimeCard
      size="sm"
      aria-labelledby={titleId}
      role="region"
      className={cn('min-w-0 gap-2', className)}
      {...props}
    >
      <DimeCardHeader
        className={cn(
          'min-h-7 items-center has-data-[slot=card-action]:grid-cols-[1fr_auto]',
          classNames.header,
        )}
      >
        <DimeCardTitle
          id={titleId}
          className={cn('text-sm font-semibold text-foreground', classNames.title)}
        >
          {title}
        </DimeCardTitle>
        {actions.length > 0 ? (
          <DimeCardAction
            className={cn(
              'col-start-2 row-span-1 row-start-1 flex gap-1 self-center justify-self-end',
              classNames.actions,
            )}
          >
            <CardShellActionButtons actions={actions} />
          </DimeCardAction>
        ) : null}
      </DimeCardHeader>
      <DimeCardContent className={cn('flex flex-col gap-2', classNames.content)}>
        {groups.length > 0 ? (
          <DimeDetailRows groups={groups} layout={layout} className={classNames.rows} />
        ) : null}
        {content}
        {groups.length === 0 && !content && empty ? (
          <div className={cn('py-2 text-center text-sm text-muted-foreground', classNames.empty)}>
            {empty}
          </div>
        ) : null}
      </DimeCardContent>
      {footer ? (
        <DimeCardFooter className={cn('flex-col items-start gap-1.5', classNames.footer)}>
          {footer}
        </DimeCardFooter>
      ) : null}
    </DimeCard>
  );
}
